import type { SessionData } from '@@/shared-components/types/Internal'
import type { Auth } from '@@/shared-components/types/Auth'
// Auth plugin
// Login a Logout metody
// Držení Laravel Sanctum tokenu v cookie
// Podporuje dvě metody přihlášení:
//   - token (výchozí): Bearer token uložený v cookie auth-token
//   - SPA: Laravel Sanctum session (httponly cookie + XSRF-TOKEN)

const tokenCookieKey = 'auth-token'
const foreignUserKey = 'foreign-user'
const expiresAtKey = 'expires-at'
const backLinkKey = 'back-link'

type LoginConfig = {
  headers?: Record<string, string>
  type?: string
  loginUrl?: string | ((baseUrl: string) => string)
  body?:
    | Record<string, unknown>
    | ((originalBody: Record<string, unknown>) => Record<string, unknown>)
}

export default defineNuxtPlugin({
  parallel: true,
  dependsOn: ['i18n:plugin', 'shared-components-plugin'],
  setup: async (nuxtApp) => {
    const { public: config } = useRuntimeConfig()

    const apiBaseUrl = config.apiBaseUrl ? config.apiBaseUrl : '$NUXT_PUBLIC_API_BASE_URL'
    const { browser, os } = useGetBrowser()
    const {
      $sentry,
      $i18n: { t },
    } = nuxtApp

    const isForeignUserCookie = useCookie(foreignUserKey, { sameSite: true, secure: true })
    const expiresAtCookie = useCookie(expiresAtKey, { sameSite: true, secure: true })

    const { addSuccessMessage, addErrorMessage } = useToastService()

    const throwMissingPermissionsError = async (permissionKey: string) => {
      const data = await checkSession()
      // Kontrola, zda oprávnění skutečně chybí
      if (data?.abilities?.find((p: string) => p === permissionKey)) {
        return
      }
      const toast = usePrimeVueToast()
      const addErrorMessage = (message: string, heading: string = 'Chyba', life = 600000) => {
        toast.add({
          severity: 'error',
          summary: heading,
          detail: message,
          life,
        })
      }

      addErrorMessage(
        `${t('Pokud si myslíte že se jedná o chybu, kontaktuje správce webu')}.`,
        `${t('Nedostatečná oprávnění')} - ${permissionKey}`
      )
      const error = new Error(t('Nedostatečná oprávnění'))
      error.code = 403
      return abortNavigation({ ...error, fatal: true })
    }
    let sessionVerified = false

    const checkSession = async () => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (auth.value.token) {
        headers['Authorization'] = `Bearer ${auth.value.token}`
      } else if (import.meta.server) {
        // In SPA mode on SSR, forward the browser's cookies so Sanctum can verify the session
        const reqHeaders = useRequestHeaders(['cookie'])
        if (reqHeaders.cookie) headers['Cookie'] = reqHeaders.cookie
      }

      const session = await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
        headers,
      })
      if (session) {
        auth.value.user = session.data.identity ?? null
        auth.value.permissions = session.data.abilities
      }
      return session.data
    }

    addRouteMiddleware(
      'auth',
      async (to) => {
        const router = useRouter()
        const loginPage = router.getRoutes().find((r) => r.meta.loginPage)?.path
        const homePage = router.getRoutes().find((r) => r.meta.homePage)?.path

        if (!to.meta.public && to.meta.middleware !== 'link') {
          if (!sessionVerified) {
            if (auth.value.user !== null && auth.value.permissions !== null) {
              sessionVerified = true
            } else {
              if (auth.value.token === null) {
                getToken()
              }
              if (auth.value.user === null) {
                await checkSession()
              }

              if (auth.value.token === null || !auth.value.permissions) {
                if (import.meta.client) {
                  localStorage.setItem(
                    backLinkKey,
                    window.location.pathname + window.location.search
                  )
                }
                return loginPage
              }
            }
          }
        }
        if (auth.value.token && to.path === loginPage) {
          return homePage
        }

        const requiredPermissions = (
          typeof to.meta.permissions === 'function'
            ? to.meta.permissions(to)
            : (to.meta.permissions ?? [])
        ) as string[]

        for (let i = 0; i < requiredPermissions.length; i++) {
          const p = requiredPermissions[i]
          if (!p || !permissions.value[p]) {
            await throwMissingPermissionsError(p)
          }
        }
        if (to.meta.logoutPage) {
          logout()
        }
      },
      { global: true }
    )

    addRouteMiddleware('guest', async (_, to) => {
      if (auth.value.token === null) {
        getToken()
      }

      if (!sessionVerified) {
        try {
          await checkSession()

          sessionVerified = true
        } catch {
          // Not logged in – allow access to guest pages
          return
        }
      }

      if (!to.meta.loginPage && auth.value.token !== null && permissions.value) {
        const toast = usePrimeVueToast()
        toast.add({
          severity: 'warn',
          summary: t('Nedostatečná oprávnění'),
          life: 600000,
        })
      }
    })
    if (nuxtApp.$sharedComponentsPlugins?.eshop) {
      const { loadBasket } = useBasketService()
      loadBasket()
    }
    const auth = useState<Auth>('auth', () => {
      return {
        user: null,
        token: null,
        permissions: null,
        foreignUser: isForeignUserCookie?.value === 'true' || false,
        expiresAt: expiresAtCookie.value ? new Date(parseInt(expiresAtCookie.value)) : null,
      }
    })

    const getToken = () => {
      auth.value.token = useCookie(tokenCookieKey, { sameSite: true, secure: true })?.value || null
    }

    const tokenCookie = useCookie(tokenCookieKey, { sameSite: true, secure: true })

    const getUser = async () => {
      if (auth.value.token) {
        const { data } = await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
          method: 'GET',
          headers: { Accept: 'application/json', Authorization: `Bearer ${auth.value.token}` },
        })
        if (!data.identity) {
          auth.value.token = undefined
          tokenCookie.value = null
          return
        }
        auth.value.permissions = data.abilities
        auth.value.user = data.identity
      }
    }

    if (auth.value.token === null) {
      getToken()
    }
    if (auth.value.user === null) {
      await getUser()
    }

    const setToken = (token: string, expires?: Date) => {
      useCookie(tokenCookieKey, { sameSite: true, secure: true, expires }).value = token
      if (expires) {
        expiresAtCookie.value = new Date(expires).getTime().toString()
        auth.value.expiresAt = new Date(expires)
      } else {
        auth.value.expiresAt = null
      }
    }

    const clearToken = () => {
      useCookie(tokenCookieKey, { sameSite: true, secure: true }).value = null
    }

    const loginAs = async (uuid: string) => {
      const { addErrorMessage } = useToastService()
      try {
        const headers: { [key: string]: string } = { Accept: 'application/json' }
        if (auth.value.token) {
          headers['Authorization'] = `Bearer ${auth.value.token}`
        }
        const { data } = await $fetch<{ data: SessionData }>(
          `${apiBaseUrl}/sessions/incarnations`,
          {
            method: 'POST',
            headers,
            body: {
              loginAs: uuid,
              tokenName: 'incarnation',
              device: {
                os,
                browser,
                type: 'eshop-client',
              },
            },
          }
        )
        await afterLoginLogic(data, true)
      } catch (e) {
        if (e?.response?._data?.errors?.[0]?.message) {
          addErrorMessage(e?.response?._data?.errors?.[0]?.message)
        }
        throw e
      }
    }

    const refetchBasket = (forceDelete: boolean = false) => {
      const { clear } = useBasketService()
      nuxtApp.runWithContext(() => {
        const queryClient = useQueryClient()
        queryClient.removeQueries({
          queryKey: ['basket'],
        })
        if (forceDelete) {
          clear()
        }
      })
    }

    const login = async (
      input:
        | {
            email: string
            password: string
          }
        | { card: string },
      config: LoginConfig = {}
    ) => {
      try {
        const { $sharedComponentsOptions } = useNuxtApp()
        const identifier = $sharedComponentsOptions?.identifier
        const optionsType = $sharedComponentsOptions?.type
        const type = identifier || optionsType ? `${identifier ?? optionsType}-client` : 'client'
        const tokenName = type
        const headers: { [key: string]: string } = {
          Accept: 'application/json',
          ...config.headers,
        }
        let credentials: 'include' | undefined = undefined
        let loginUrl = `${apiBaseUrl}/sessions`
        let body: Record<string, unknown> = {}
        if ('card' in input) {
          body = {
            card: input.card,
            tokenName: 'card',
            device: {
              os,
              browser,
              type,
            },
          }
          loginUrl = `${apiBaseUrl}/sessions/cards`
        } else {
          body = {
            username: input?.email,
            password: input?.password,
            tokenName,
            device: {
              os,
              browser,
              type,
            },
          }
        }
        if ($sharedComponentsOptions?.options?.auth?.sanctumSpa && !('card' in input)) {
          const url = new URL(apiBaseUrl)
          // Step 1: Obtain the XSRF-TOKEN cookie from Laravel Sanctum
          await $fetch(`${url.origin}/sanctum/csrf-cookie`)
          // Step 2: Read the XSRF-TOKEN value – it is not httponly so JS can read it
          const xsrfMatch = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
          const xsrfToken = xsrfMatch ? decodeURIComponent(xsrfMatch[1]!) : ''
          // Step 3: Authenticate via SPA endpoint (Laravel sets the httponly session cookie)
          headers['X-XSRF-TOKEN'] = xsrfToken
          loginUrl = `${apiBaseUrl}/sessions/spa`
          credentials = 'include'
          body = {
            username: input.email,
            password: input.password,
            device: {
              os,
              browser,
              type,
            },
          }
        }

        if (config.body) {
          if (typeof config.body === 'function') {
            body = config.body(body)
          } else {
            body = config.body
          }
        }
        if (config.loginUrl) {
          if (typeof config.loginUrl === 'function') {
            loginUrl = config.loginUrl(apiBaseUrl)
          } else {
            loginUrl = config.loginUrl
          }
        }
        const { data } = await $fetch<{ data: SessionData }>(loginUrl, {
          method: 'POST',
          headers,
          body,
          credentials,
        })

        await afterLoginLogic(data)
      } catch (e) {
        throw e
      }
    }

    const afterLoginLogic = async (data: SessionData, foreignUser = false) => {
      if (data.token) {
        setToken(data.token, data.expiresAt ? new Date(data.expiresAt.iso) : undefined)
        auth.value.token = data.token
      }
      if (data.identity) {
        auth.value.user = data.identity
      }
      if (data.abilities) {
        auth.value.permissions = data.abilities
      }
      auth.value.foreignUser = foreignUser
      isForeignUserCookie.value = foreignUser ? 'true' : 'false'

      if (nuxtApp.$sharedComponentsPlugins?.eshop) {
        const { basket } = useBasketService()
        if (basket.value.guid) {
          refetchBasket()
        } else {
          // Pokud nemám košík načtu košíky uživatele a dám je do state
          const userBasketsResponse = await useCustomFetch<{ data: object[] }>({
            url: '/self/baskets',
            query: {
              open: 1,
            },
            clientOnly: true,
          })
          const userBaskets = useState('user-baskets', () => [])
          userBaskets.value = userBasketsResponse.data ?? []
        }
        const { userLogin: dataLayerUserLogin } = useDataLayers()
        dataLayerUserLogin(auth.value.user!)
      }

      if (
        $sentry &&
        typeof $sentry === 'object' &&
        'setUser' in $sentry &&
        typeof $sentry.setUser === 'function'
      ) {
        $sentry.setUser({
          username: data.identity.username,
          email: data.identity.email,
          id: data.identity.id,
        })
      }

      if (data.identity && nuxtApp.$sharedComponentsPlugins?.firebase) {
        // Zajstí, že se vygeneroval vždy nový a né starý FCM token
        const registerFCM = async () => {
          const { getToken: getFcmToken, deleteToken } = useFcm()
          const token = await getFcmToken()
          if (token) {
            const printerHash = window.printerHash
            await deleteToken(token, 'all')
            const newPushToken = await getFcmToken()
            // Kontrola, zda se uživatel již neodhlásil
            if (auth.value.user) {
              if (newPushToken) {
                const promises = [registerPushToken(newPushToken, 'profile')]
                if (printerHash) {
                  promises.push(registerPushToken(newPushToken, 'device', printerHash))
                }
                await Promise.all(promises)
              }
            }
          }
        }
        registerFCM()
      }

      const localeBackLink = getBackLink()
      const router = useRouter()

      const backLink = localeBackLink
        ? localeBackLink
        : router.getRoutes().find((r) => r.meta.afterLoginPage)?.path ||
          router.getRoutes().find((r) => r.meta.homePage)?.path

      removeBackLink()
      if (backLink) {
        appNavigateTo(backLink)
      } else {
        const toast = usePrimeVueToast()
        toast.add({
          severity: 'warn',
          summary: t('Nedostatečná oprávnění'),
          detail: t('Nemáte oprávnění ani do ERP, ani do Kiosku.'),
          life: 600000,
        })
        showError(
          createError({
            statusMessage: t('Nedostatečná oprávnění'),
            statusText: t('Nemáte oprávnění ani do ERP, ani do Kiosku.'),
            statusCode: 403,
            fatal: true,
          })
        )
      }
    }

    const logout = async (logoutOnBackend: boolean = true) => {
      try {
        if (nuxtApp.$sharedComponentsPlugins?.firebase) {
          const { fcmToken, deleteToken } = useFcm()
          if (fcmToken.value) {
            try {
              await deleteToken(fcmToken.value, 'profile')
            } catch {}
          }
        }
        if (logoutOnBackend) {
          try {
            const headers: Record<string, string> = { Accept: 'application/json' }
            if (auth.value.token) {
              headers['Authorization'] = `Bearer ${auth.value.token}`
            } else {
              const xsrfMatch = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
              const xsrfToken = xsrfMatch ? decodeURIComponent(xsrfMatch[1]!) : ''
              if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken
            }

            await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
              method: 'DELETE',
              headers,
            })
          } catch (e) {
            console.error(e)
          }
        }
        clearToken()
        if (import.meta.client) {
          tokenCookie.value = null
        }
        auth.value = {
          permissions: null,
          token: null,
          user: null,
          expiresAt: null,
        }
        if (
          $sentry &&
          typeof $sentry === 'object' &&
          'setUser' in $sentry &&
          typeof $sentry.setUser === 'function'
        ) {
          $sentry.setUser(null)
        }
        const localePath = useLocalePath()
        if (nuxtApp.$sharedComponentsPlugins?.eshop) {
          refetchBasket(true)
        }
        nuxtApp.runWithContext(() => {
          const queryClient = useQueryClient()
          queryClient.invalidateQueries({ refetchType: 'none' })
        })
        addSuccessMessage(t('Odhlášení proběhlo úspěšně'))
        const router = useRouter()
        const loginPage = router.getRoutes().find((r) => r.meta.loginPage)?.path
        appNavigateTo(loginPage ?? localePath({ name: 'login' }))
      } catch (e) {
        addErrorMessage(t('Při odhlášení došlo k chybě'))
        console.error(e)
      }
    }

    const isRoot = computed(() => {
      return auth.value?.permissions && auth.value?.permissions?.includes('root')
    })

    const permissions = computed(() => {
      const permissionsObject: { [key: string]: boolean } = {}
      ;(auth.value?.permissions ?? []).forEach((p: string) => {
        permissionsObject[p] = true
      })
      return permissionsObject
    })

    nuxtApp.vueApp.directive('can', {
      beforeMount(el, binding) {
        const comment = document.createComment('v-can')
        el.__vueCanPlaceholder__ = comment
        if (!(binding.value ?? []).some((p: string) => permissions.value[p])) {
          if (el.parentNode) {
            el.parentNode.replaceChild(comment, el)
          }
        }
      },
      updated(el, binding) {
        const comment = el.__vueCanPlaceholder__

        if ((binding.value ?? []).some((p: string) => permissions.value[p])) {
          if (comment.parentNode) {
            comment.parentNode.replaceChild(el, comment)
          }
        } else {
          if (el.parentNode) {
            el.parentNode.replaceChild(comment, el)
          }
        }
      },
    })

    const getBackLink = () => {
      const link = localStorage.getItem(backLinkKey)
      if (link) {
        try {
          let serializedValue
          try {
            serializedValue = JSON.parse(link)
          } catch {
            serializedValue = link
          }
          return serializedValue
        } catch (e) {
          console.error(e)
        }
      }
      return null
    }

    const setBackLink = (value: string) => {
      localStorage.setItem(backLinkKey, value)
    }

    const removeBackLink = () => {
      localStorage.removeItem(backLinkKey)
    }

    return {
      provide: {
        auth: {
          auth,
          login,
          loginAs,
          logout,
          afterLoginLogic,
          isRoot,
          permissions,
          checkSession,
          getBackLink,
          setBackLink,
          removeBackLink,
        },
      },
    }
  },
})
